export function extractSchemaKey(schema) {
    if (!schema.description) {
        throw new Error('Schema must have a description.');
    }
    const parsedDescription = JSON.parse(schema.description);
    if (!parsedDescription._schema_key) {
        throw new Error('Schema description must contain a _schema_key.');
    }
    return parsedDescription._schema_key;
}
export function getSchemaVersion(schema) {
    const schemaKey = extractSchemaKey(schema);
    const versionRegex = /([^/]+)$/;
    const match = schemaKey.match(versionRegex);
    // If there's no match or the last segment doesn't have any characters after the slash, return empty string
    if (!match || match[1] === '') {
        throw new Error(`Invalid schema key format: ${schemaKey}`);
    }
    return match[1];
}
export function getBigQueryColumnName(schema) {
    if (!schema.description) {
        throw new Error('Schema must have a description.');
    }
    const schemaKey = extractSchemaKey(schema);
    const parsedDescription = JSON.parse(schema.description);
    const type = parsedDescription.meta.schemaType;
    const regex = /:([^/]+)\/([^/]+)(?:\/[^/]+)*\/([^-/]+)(?:-.*)?$/;
    const match = schemaKey.match(regex);
    if (!match) {
        throw new Error(`Invalid schema key format: ${schemaKey}`);
    }
    const vendor = match[1].replace(/\./g, '_');
    const event = match[2];
    const majorVersion = match[3];
    const prefix = type === 'event' ? 'unstruct_event' : 'contexts';
    return `${prefix}_${vendor}_${event}_${majorVersion}`;
}
let trackSelfDescribingEvent;
/**
 * Tracks a self-describing event with strongly typed validation using Zod schemas and logs any runtime zod errors.
 *
 * This function provides a type-safe wrapper around the Snowplow `trackSelfDescribingEvent` function.
 * It ensures that the event and context data conform to their respective schemas before tracking.
 * Handles both JavaScript tag version (window.snowplow) and npm version (trackSelfDescribingEvent).
 *
 * @param event - The event object containing a Zod schema and data conforming to that schema.
 * @param context - An array of context objects, each with its own Zod schema and matching data.
 * @param safeParse - Optional boolean indicating whether to use Zod's `safeParse` (error-tolerant validation). Defaults to true.
 * @param event - The event object containing a Zod schema and data conforming to that schema.
 * @param context - An array of context objects, each with its own Zod schema and matching data.
 * @param safeParse - Optional boolean indicating whether to use Zod's `safeParse` (error-tolerant validation). Defaults to true.
 *
 * @example
 * trackSelfDescribingEventTypeSafe({
 *   event: {
 *     schema: exampleBasicEventEventSchemaV1,
 *     data: { id: 123, name: 'example event' },
 *   },
 *   context: [
 *     { schema: exampleBasicEntityEntitySchemaV1, data: { id: 123, name: 'example' } },
 *     { schema: exampleAnotherBasicEntityEntitySchemaV1, data: { id: 123, name: 'another-example' } }
 *   ],
 * });
 */
export const trackSelfDescribingEventTypeSafe = async ({ event, context, safeParse = true, trackers, }) => {
    validateData(event.schema, event.data, 'Event', undefined, safeParse);
    context.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index, safeParse);
    });
    const eventSchemaKey = extractSchemaKey(event.schema);
    const contextSchemas = context.map((ctx) => ({
        schema: extractSchemaKey(ctx.schema),
        data: ctx.data,
    }));
    // Try JavaScript tag version first (for apps using the Snowplow tag)
    if (window.snowplow) {
        window.snowplow('trackSelfDescribingEvent', {
            event: {
                schema: eventSchemaKey,
                data: event.data,
            },
            context: contextSchemas,
        });
        return;
    }
    if (!trackSelfDescribingEvent) {
        // Lazy load the self-describing event tracking function to avoid unnecessary imports
        trackSelfDescribingEvent = (await import('@snowplow/browser-tracker')).trackSelfDescribingEvent;
    }
    trackSelfDescribingEvent({
        event: {
            schema: eventSchemaKey,
            data: event.data,
        },
        context: contextSchemas,
    }, trackers);
};
let trackPageView;
export function validateData(schema, data, type, index, safeParse = true) {
    const validationResult = safeParse ? schema.safeParse(data) : schema.parse(data);
    const schemaKey = extractSchemaKey(schema);
    if (!validationResult.success) {
        const contextInfo = index !== undefined ? ` for context at index ${index}` : '';
        console.warn(`${type} ${schemaKey} validation failed${contextInfo}:`, validationResult.error);
        return false;
    }
    return true;
}
export const trackPageViewTypeSafe = async ({ trackers, context, safeParse = true, }) => {
    context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index, safeParse);
    });
    if (!trackPageView) {
        // Lazy load the button click tracking plugin to avoid unnecessary imports
        trackPageView = (await import('@snowplow/browser-tracker')).trackPageView;
    }
    trackPageView({
        context: context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        })),
    }, trackers);
};
let enableButtonClickTracking;
export const enableButtonClickTrackingTypeSafe = async (config) => {
    config?.context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    if (!enableButtonClickTracking) {
        // Lazy load the button click tracking plugin to avoid unnecessary imports
        enableButtonClickTracking = (await import('@snowplow/browser-plugin-button-click-tracking'))
            .enableButtonClickTracking;
    }
    enableButtonClickTracking({
        context: config?.context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        })),
    });
};
let enableLinkClickTracking;
export const enableLinkClickTrackingTypeSafe = async (config) => {
    config?.context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    if (!enableLinkClickTracking) {
        // Lazy load the link click tracking plugin to avoid unnecessary imports
        enableLinkClickTracking = (await import('@snowplow/browser-plugin-link-click-tracking')).enableLinkClickTracking;
    }
    enableLinkClickTracking({
        context: config?.context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        })),
        trackContent: config?.trackContent,
    });
};
/**
 * Helper function to create wrapper for dynamic context functions
 */
function createDynamicContextWrapper(contextFunction, index) {
    return (...args) => {
        const contextParams = parseFormEventParams(args);
        if (!contextParams) {
            return null;
        }
        const contextResult = contextFunction(contextParams);
        if (contextResult === null || !contextResult) {
            return null;
        }
        // Validate the function result at event time
        const isValid = validateData(contextResult.schema, contextResult.data, 'Dynamic Context', index);
        if (!isValid) {
            return null;
        }
        return {
            schema: extractSchemaKey(contextResult.schema),
            data: contextResult.data,
        };
    };
}
/**
 * Parse Snowplow form event parameters into typed objects
 */
function parseFormEventParams(args) {
    if (args.length === 3) {
        // Form change/focus events: (element, eventType, value)
        return {
            element: args[0],
            eventType: args[1],
            value: args[2],
        };
    }
    else if (args.length === 2) {
        // Form submission events: (element, innerElements)
        return {
            element: args[0],
            innerElements: args[1],
        };
    }
    console.warn('Unexpected form context function parameters:', args);
    return null;
}
/**
 * Type guard to check if context item is a function
 */
function isDynamicContext(ctx) {
    return typeof ctx === 'function';
}
let enableFormTracking;
/**
 * Enable form tracking with type-safe context validation.
 * Supports both static context objects and dynamic context functions.
 *
 * @param config - Configuration object with optional context array
 * @param config.context - Array of context items (static objects or dynamic functions)
 *
 * @example
 * // Static context
 * enableFormTrackingTypeSafe({
 *   context: [{ schema: mySchema, data: { userId: 123 } }]
 * });
 *
 * @example
 * // Dynamic context function
 * enableFormTrackingTypeSafe({
 *   context: [
 *     ({ element, eventType, value }) => ({
 *       schema: myDynamicSchema,
 *       data: { elementId: element.id, eventType, value }
 *     })
 *   ]
 * });
 */
export const enableFormTrackingTypeSafe = async (config) => {
    if (!enableFormTracking) {
        // Lazy load the form tracking plugin to avoid unnecessary imports
        enableFormTracking = (await import('@snowplow/browser-plugin-form-tracking')).enableFormTracking;
    }
    if (!config?.context) {
        // Extract valid FormTrackingConfiguration properties when no context
        const { context: _, ...formTrackingConfig } = config ?? {};
        enableFormTracking(formTrackingConfig);
        return;
    }
    // Validate static context immediately
    config.context.forEach((ctx, index) => {
        if (!isDynamicContext(ctx)) {
            validateData(ctx.schema, ctx.data, 'Context', index);
        }
    });
    const processedContext = config.context.map((ctx, index) => {
        if (isDynamicContext(ctx)) {
            return createDynamicContextWrapper(ctx, index);
        }
        // Static context object
        return {
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        };
    });
    // Extract valid FormTrackingConfiguration properties
    const { context: _, ...formTrackingConfig } = config;
    enableFormTracking({
        ...formTrackingConfig,
        context: processedContext,
    });
};
let trackSiteSearch;
/**
 * Tracks a site search event with the provided terms and optional filters and metrics.
 *
 * @param params Configuration for the site search tracking
 * @param params.terms Search terms entered by the user as an array
 * @param params.filters Optional filters applied to the search (e.g., categories)
 * @param params.totalResults Optional number of search results found
 * @param params.pageResults Optional number of search results displayed on the page
 * @param params.context Optional context entities to attach to the event
 * @param params.trackers Optional array of tracker identifiers
 */
export const trackSiteSearchTypeSafe = async ({ terms, filters, totalResults, pageResults, context, trackers, }) => {
    context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    if (!trackSiteSearch) {
        trackSiteSearch = (await import('@snowplow/browser-plugin-site-tracking')).trackSiteSearch;
    }
    trackSiteSearch({
        terms,
        filters,
        totalResults,
        pageResults,
        context: context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        })),
    }, trackers);
};
//# sourceMappingURL=data:application/json;base64,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